import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, InputAdornment, Stack, Tabs } from '@mui/material';
import { calendrierAssistanceCapitalisationDefaultValues } from '@norma/shared/constants/prejudice/defaultValues/FormCalendrierAssistanceCapitalisation.defaultValues';
import {
  getCapitalisationBaremes,
  getShouldNotDisplayCapitalisation,
} from '@norma/shared/helpers/prejudices/capitalisation';
import { PrejudiceFormCalendrierAssistanceCapitalisation } from '@norma/shared/types/prejudice.type';
import i18next from 'i18next';
import { intersection } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CalculsBox } from 'src/components/basic/CalculsBox';
import { DintilhacText } from 'src/components/client/prejudiceFormComponents/DintilhacText';
import { ExpandableTextFieldForm } from 'src/components/forms/ExpandableTextFieldForm';
import { validationSchemaCalendrierAssistanceCapitalisation } from 'src/constants/prejudice/validation/FormCalendrierAssistanceCapitalisation.validation';
import { PrejudiceContainer } from '../PrejudiceContainer';
import { TotalCapitalisation } from '../prejudiceFormComponents/Capitalisation/TotalCapitalisation';
import { Chiffrage } from '../prejudiceFormComponents/Chiffrage';
import { DintilhacButton } from '../prejudiceFormComponents/DintilhacButton';
import { TableAssistance } from '../prejudiceFormComponents/TableAssistance/TableAssistance';
import { TotalAssistanceDebitSolde } from '../prejudiceFormComponents/TotalAssistanceDebitSolde';
import { PrejudiceFormProps } from '../PrejudiceFormProps';
import { PrejudiceTab } from '../PrejudiceTab';
import { SavePrejudiceButton } from '../SavePrejudiceButton';

type Props =
  PrejudiceFormProps<PrejudiceFormCalendrierAssistanceCapitalisation>;

export const FormCalendrierAssistanceCapitalisation: React.FC<Props> = ({
  victime,
  procedure,
  values,
  monetaryErosions,
  baremes,
  dateLiquidation,
  allNumerosPieces,
  dateDeces,
  dateConsolidation,
  dateAccident,
  ...props
}) => {
  const [activeTab, setActiveTabs] = useState(0);

  const capitalisationBaremes = useMemo(
    () => getCapitalisationBaremes(baremes, victime.sexe),
    [baremes, victime.sexe],
  );

  const useFormReturn =
    useForm<PrejudiceFormCalendrierAssistanceCapitalisation>({
      defaultValues: calendrierAssistanceCapitalisationDefaultValues({
        procedure,
        values,
        monetaryErosions,
        victime,
        baremes,
        isExtractedValues: false,
      }),
      resolver: yupResolver(
        validationSchemaCalendrierAssistanceCapitalisation({
          procedure,
          victime,
          prejudiceType: props.prejudiceType,
          dateAccident,
          dateConsolidation,
          dateLiquidation,
          dateDeces,
        }),
      ),
    });
  const {
    control,
    formState: { errors },
  } = useMemo(() => useFormReturn, [useFormReturn]);
  const { t } = useTranslation();
  const isFirstTabError = useMemo(() => {
    const firstTabFields: (keyof PrejudiceFormCalendrierAssistanceCapitalisation)[] =
      ['rows', 'notes'];
    return (
      intersection(Object.keys(errors), firstTabFields).length > 0 &&
      activeTab !== 0
    );
  }, [errors, activeTab]);

  const isSecondTabError = useMemo(() => {
    const secondTabFields: (keyof PrejudiceFormCalendrierAssistanceCapitalisation)[] =
      [
        'victimeSommeACapitaliser',
        'ageDernierArrerage',
        'isLastArrerageViager',
        'baremeCapitalisation',
        'victimeCoefficientCapitalisation',
        'capitalisationTiersPayeurs',
        'commentaires',
        'victimeTotalCapitalise',
      ];
    return (
      intersection(Object.keys(errors), secondTabFields).length > 0 &&
      activeTab !== 1
    );
  }, [errors, activeTab]);
  const shouldNotDisplayCapitalisation = getShouldNotDisplayCapitalisation({
    dateConsolidation,
    dateLiquidation,
    dateDeces,
  });

  const [showDintilhac, setShowDintilhac] = React.useState<boolean>(false);

  return (
    <PrejudiceContainer<PrejudiceFormCalendrierAssistanceCapitalisation>
      {...props}
      procedure={procedure}
      victime={victime}
      monetaryErosions={monetaryErosions}
      baremes={baremes}
      {...useFormReturn}
      renderPrejudice={() => (
        <Stack direction="column" spacing={4} flex={1}>
          <ExpandableTextFieldForm
            control={control}
            name="notes"
            label={t('prejudice.fields.notes.label')}
            placeholder={t('prejudice.fields.notes.placeholder') || ''}
            dialogTitle={t('prejudice.fields.notes.label')}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <DintilhacButton onClick={() => setShowDintilhac(true)} />
                </InputAdornment>
              ),
            }}
            multiline
            maxRows={4}
            fullWidth
          />
          <DintilhacText
            open={showDintilhac}
            setOpen={setShowDintilhac}
            label={t('prejudice.fields.dintilhac.label')}
            content={i18next.t(
              `prejudice.prejudicesTypes.${props.prejudiceType}.introduction`,
              {
                defaultValue: '',
              },
            )}
          />
          <Tabs value={activeTab} onChange={(_, value) => setActiveTabs(value)}>
            <PrejudiceTab label="ATPF" value={0} isError={isFirstTabError} />
            {!shouldNotDisplayCapitalisation ? (
              <PrejudiceTab
                label="Capitalisation"
                value={1}
                isError={isSecondTabError}
              />
            ) : null}
            <PrejudiceTab label="ATPF Récapitulatif" value={2} />
          </Tabs>
          <Stack sx={{ display: activeTab === 0 ? '' : 'none' }}>
            {activeTab === 0 && (
              <TableAssistance
                control={control}
                tiersPayeurs={procedure.tiersPayeurs}
                hospitalisations={procedure.hospitalisations}
                dateConsolidation={
                  procedure?.dateConsolidation
                    ? new Date(procedure.dateConsolidation)
                    : undefined
                }
                dateLiquidation={dateLiquidation}
                monetaryErosions={monetaryErosions}
                isCalculCapitalisation
                prejudiceType={props.prejudiceType}
                procedure={procedure}
                victime={victime}
                allNumerosPieces={allNumerosPieces}
                shouldNotDisplayCapitalisation={shouldNotDisplayCapitalisation}
                dateDeces={dateDeces}
              />
            )}
          </Stack>
          {!shouldNotDisplayCapitalisation ? (
            <Grid
              container
              sx={{ display: activeTab === 1 ? '' : 'none' }}
              spacing={1}
            >
              <Grid item md={8}>
                <TotalCapitalisation
                  control={control}
                  victimeName={{
                    sommeACapitaliser: 'victimeSommeACapitaliser',
                    bareme: 'baremeCapitalisation',
                    coefficient: 'victimeCoefficientCapitalisation',
                    montantCapitalise: 'victimeTotalCapitalise',
                    isLastArrerageViager: 'isLastArrerageViager',
                    ageDernierArrerage: 'ageDernierArrerage',
                    ageDateAttribution: 'ageDateAttribution',
                  }}
                  procedure={procedure}
                  dateLiquidation={
                    dateLiquidation ? new Date(dateLiquidation) : null
                  }
                  baremes={capitalisationBaremes}
                  dateNaissance={
                    victime.dateNaissance
                      ? new Date(victime.dateNaissance)
                      : null
                  }
                  dateDeces={
                    victime.dateDeces ? new Date(victime.dateDeces) : null
                  }
                  sexe={victime.sexe}
                  tiersPayeursName={{
                    root: 'capitalisationTiersPayeurs.parTiersPayeur',
                    montantCapitalise: 'tiersPayeursTotalCapitalise',
                  }}
                  tiersPayeurSuffix={{
                    sommeACapitaliser: 'sommeACapitaliser',
                    montantCapitalise: 'montantCapitalise',
                    coefficient: 'coefficient',
                  }}
                  defaultValuesNames={{
                    victimeSommeACapitaliser: 'totalMontantTotalAnnualise',
                  }}
                  displayTotal
                  editedFieldsName="editedFields"
                />
              </Grid>
              <Grid item md={4}>
                <CalculsBox>
                  <Stack width={'98%'}>
                    <ExpandableTextFieldForm
                      control={control}
                      name="commentaires"
                      label="commentaires"
                      dialogTitle={t('prejudice.fields.notes.label')}
                      multiline
                      inputProps={{
                        sx: { minHeight: '200px' },
                      }}
                      maxRows={9}
                      fullWidth
                    />
                  </Stack>
                </CalculsBox>
              </Grid>
            </Grid>
          ) : null}
          <Box sx={{ display: activeTab === 2 ? '' : 'none' }}>
            <TotalAssistanceDebitSolde
              control={control}
              tiersPayeurs={procedure.tiersPayeurs}
              partResponsabilite={procedure.partResponsabilite}
              dateLiquidation={dateLiquidation}
              monetaryErosions={monetaryErosions}
              dateConsolidation={
                procedure.dateConsolidation
                  ? new Date(procedure.dateConsolidation)
                  : undefined
              }
              dateDeces={
                victime.dateDeces ? new Date(victime.dateDeces) : undefined
              }
            />
          </Box>
          <Grid container>
            <Grid item xs={4}>
              <Chiffrage control={control} />
            </Grid>
            <Grid
              item
              xs={4}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <SavePrejudiceButton victime={victime} />
            </Grid>
            <Grid item xs={4} />
          </Grid>
        </Stack>
      )}
    />
  );
};
